import { ValidationIssue } from '@decisions/server/DecisionsFramework';
import { newMappingContext } from '@decisions/server/DecisionsFramework/Design/Flow/Service';
import {
  DataDesignerSurfaceType,
  DiagramDisplayData,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData';
import { IRect, IPoint, isRectBetweenPoints, getBoundRect } from 'utils/GeometryUtils';
import { DataDesignerReducerState } from './DataDesignerReducer';

export const SimpleDiagramHeight = 35;
export const DefaultDiagramWidth = 370;
export const DefaultHorizontalGap = 50;

export function getHelpCenterValidations(state: DataDesignerReducerState): ValidationIssue[] {
  if (state.diagrams.length === 0) {
    return [];
  }

  const lastDiagram = state.diagrams[state.diagrams.length - 1];
  const lastRowName = lastDiagram.DataDescription.Name;

  let diagrams = state.diagrams;
  // When we are in List View last row always has validation as it has empty name,
  // but from user's point of view it is completely valid state, so we don't need to show validation
  if (state.currentView === 'List View' && !lastRowName) {
    diagrams = diagrams.filter(item => item.Id !== lastDiagram.Id);
  }

  return diagrams.flatMap(item => item.ValidationIssues);
}

export function getDataDiagramRect(diagram: DiagramDisplayData, isExpanded: boolean): IRect {
  const view = diagram.View;
  return {
    top: view.Top,
    left: view.Left,
    height: getDiagramHeight(diagram, isExpanded),
    width: view.Width,
  };
}

/**
 * Returns filtered array of identifiers which exist in "newDiagrams" argument.
 */
export function syncDiagramsIds(newDiagrams: DiagramDisplayData[], diagramIds: string[]): string[] {
  return diagramIds.filter(selectedItemName => newDiagrams.some(diagram => diagram.Id === selectedItemName));
}

export function isSimpleOrCollapsed(diagram: DiagramDisplayData, isExpanded: boolean) {
  return !hasChildProperties(diagram) || !isExpanded;
}

export function getDiagramsByIds(allDiagrams: DiagramDisplayData[], selectedDiagramIds: string[]) {
  return allDiagrams.filter(item => selectedDiagramIds.includes(item.Id));
}

export function getDiagramById(allDiagrams: DiagramDisplayData[], id: string) {
  return allDiagrams.find(item => item.Id === id);
}

export function hasDiagram(allDiagrams: DiagramDisplayData[], id: string) {
  return allDiagrams.some(item => item.Id === id);
}

export function getSelectionDiagramsRect(diagrams: DiagramDisplayData[], expandedDiagramIds: string[]) {
  const rects = diagrams.map(diagram => {
    const isExpanded = expandedDiagramIds.some(id => id === diagram.Id);
    return getDataDiagramRect(diagram, isExpanded);
  });

  return getBoundRect(rects);
}

export function getSelectedDiagrams(
  diagrams: DiagramDisplayData[],
  expandedDiagramIds: string[],
  startPoint: IPoint,
  endPoint: IPoint
): DiagramDisplayData[] {
  return diagrams.filter(item => {
    const isExpanded = expandedDiagramIds.some(id => id === item.Id);

    return (
      isRectBetweenPoints(getDataDiagramRect(item, isExpanded), startPoint, endPoint) ||
      isRectBetweenPoints(getDataDiagramRect(item, isExpanded), endPoint, startPoint)
    );
  });
}

export function hasChildProperties(diagram: DiagramDisplayData): boolean {
  return diagram.DataDescription.HasReadableSubElements;
}

export function getDiagramHeight(diagram: DiagramDisplayData, isExpanded: boolean) {
  return isSimpleOrCollapsed(diagram, isExpanded) ? SimpleDiagramHeight : diagram.View.Height;
}

export function getDesignPatternMappingContext(designPatternId: string) {
  return newMappingContext({
    ElementId: designPatternId,
    HandlerName: 'DecisionsFramework.Design.DesignPattern.DeclaredInterfaceContextHandler',
  });
}

export function getNoDiagramsMessage(surfaceType: DataDesignerSurfaceType) {
  const dataType = surfaceType === DataDesignerSurfaceType.Input ? 'input' : 'output';

  return `drag and drop a type from toolbox here to create ${dataType} data`;
}

export function getNoDiagramNameMessage(surfaceType: DataDesignerSurfaceType) {
  const dataType = surfaceType === DataDesignerSurfaceType.Input ? 'Input' : 'Output';

  return `[ Set ${dataType} Name ]`;
}
